/**
 * Générateur — Il calcule les volumes d'assemblages de solides étudiés au
 * cours du cycle.
 * Attendu ID : 232 | Niveau : 3ème | Domaine : Grandeurs (d18, O2)
 *
 * Source doc prof (CS1 + Créés n°10-11). Plan auto-validé 2026-07-17 (TODO
 * §6) : « silo » (cylindre + cône), « gelule » (cylindre + 2 demi-sphères =
 * cylindre + boule — le réservoir du référentiel), « evide » (demi-boule
 * évidée d'un cylindre : soustraction), « boules » (3 boules dans la boîte :
 * le reste vaut TOUJOURS 1/3 — indépendant du rayon). Coefficients de π en
 * trous séparés, arrondis partagés (garde anti-pivot).
 */

import { randomChoice, randInt, tex, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 232;
const L_SILO = 'Volume d\'un silo (cylindre + cône)';
const L_GELULE = 'Volume d\'un réservoir (cylindre + demi-sphères)';
const L_EVIDE = 'Volume d\'un moule (demi-boule évidée)';
const L_BOULES = 'Trois boules dans une boîte cylindrique';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const arrondiSur = (x) => {
    const frac = x - Math.floor(x);
    if (Math.abs(frac - 0.5) < 0.03) return null;
    return Math.round(x);
};

// ═════════ « silo » — cylindre + cône ═════════

const genSiloTrous = () => {
    let r; let h1; let h2; let coef; let approx;
    for (let essai = 0; essai < 80; essai++) {
        r = randInt(2, 6);
        h1 = randInt(6, 12);
        h2 = 3 * randInt(1, 3);                            // hauteur du cône multiple de 3
        coef = r * r * h1 + (r * r * h2) / 3;              // V = coef·π
        approx = arrondiSur(coef * Math.PI);
        if (approx !== null) break;
        approx = null;
    }
    if (approx === null) { r = 3; h1 = 10; h2 = 3; coef = 99; approx = 311; }
    const phrase = `$V = {{${coef}}} \\pi\\ \\text{m}^{3} \\approx {{${approx}}}\\ \\text{m}^{3}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un silo à grains est formé d'un cylindre de rayon ${r} m et de hauteur ${h1} m, surmonté `
        + `d'un cône de même rayon et de hauteur ${h2} m.</p>`
        + `<p>Le volume d'un cône vaut ${tex('\\dfrac{\\pi r^{2} h}{3}')}.</p>`
        + '<p><b>Donne le volume exact en complétant le coefficient de π, puis l\'arrondi au m³.</b></p>',
        { ...opts(L_SILO), mode: 'saisie' });
    payload._meta.params = { r, h1, h2, coef, approx };
    return payload;
};

// ═════════ « gelule » — cylindre + boule ═════════

const genGeluleTrous = () => {
    let r; let h; let coefCyl; let coefBoule; let approx;
    for (let essai = 0; essai < 80; essai++) {
        r = 3 * randInt(1, 3);                             // multiple de 3 → boule entière
        h = randInt(5, 12);
        coefCyl = r * r * h;
        coefBoule = (4 * r * r * r) / 3;
        approx = arrondiSur((coefCyl + coefBoule) * Math.PI);
        if (approx !== null && coefCyl !== coefBoule) break;
        approx = null;
    }
    if (approx === null) { r = 3; h = 8; coefCyl = 72; coefBoule = 36; approx = 339; }
    const phrase = `$\\text{Cylindre : } {{${coefCyl}}} \\pi\\ \\text{cm}^{3} \\qquad`
        + ` \\text{Demi-sphères : } {{${coefBoule}}} \\pi\\ \\text{cm}^{3} \\qquad`
        + ` \\text{Total} \\approx {{${approx}}}\\ \\text{cm}^{3}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un réservoir est formé d'un cylindre de rayon ${r} cm et de hauteur ${h} cm, avec une `
        + 'DEMI-sphère de même rayon à chaque extrémité. Les deux demi-sphères forment une boule.</p>'
        + '<p><b>Donne les deux volumes exacts en complétant les coefficients de π, puis le total '
        + 'arrondi au cm³.</b></p>',
        { ...opts(L_GELULE), mode: 'saisie' });
    payload._meta.params = { r, h, coefCyl, coefBoule, approx };
    return payload;
};

// ═════════ « evide » — demi-boule − cylindre ═════════

const genEvideTrous = () => {
    let R; let r; let h; let coefDemi; let coefCyl; let approx;
    for (let essai = 0; essai < 80; essai++) {
        R = 3 * randInt(2, 4);                             // demi-boule, multiple de 3
        r = randInt(2, R - 2);
        h = randInt(2, 4);
        coefDemi = (2 * R * R * R) / 3;
        coefCyl = r * r * h;
        if (coefCyl >= coefDemi) continue;
        approx = arrondiSur((coefDemi - coefCyl) * Math.PI);
        if (approx !== null) break;
        approx = null;
    }
    if (approx === null) { R = 9; r = 4; h = 3; coefDemi = 486; coefCyl = 48; approx = 1376; }
    const phrase = `$\\text{Demi-boule : } {{${coefDemi}}} \\pi\\ \\text{cm}^{3} \\qquad`
        + ` \\text{Cylindre : } {{${coefCyl}}} \\pi\\ \\text{cm}^{3} \\qquad`
        + ` \\text{Métal} \\approx {{${approx}}}\\ \\text{cm}^{3}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un moule en métal est une demi-boule de rayon ${R} cm dans laquelle on a évidé un cylindre `
        + `de rayon ${r} cm et de hauteur ${h} cm.</p>`
        + `<p>Le volume d'une demi-boule de rayon R vaut ${tex('\\dfrac{2}{3} \\pi R^{3}')}.</p>`
        + '<p><b>Donne les deux volumes exacts en complétant les coefficients de π, puis le volume '
        + 'de métal arrondi au cm³.</b></p>',
        { ...opts(L_EVIDE), mode: 'saisie' });
    payload._meta.params = { R, r, h, coefDemi, coefCyl, approx };
    return payload;
};

// ═════════ « boules » — 3 boules dans la boîte (1/3 restant) ═════════

const genBoulesTrous = () => {
    const r = randInt(2, 9);
    const objet = randomChoice(['boules de pétanque', 'balles rebondissantes', 'boules décoratives']);
    const coefBoite = 6 * r * r * r;                       // πr² × 6r
    const coefBoules = 4 * r * r * r;                      // 3 × (4/3)πr³
    const coefReste = coefBoite - coefBoules;              // = 2r³
    const phrase = `$\\text{Boîte : } {{${coefBoite}}} \\pi\\ \\text{cm}^{3} \\qquad`
        + ` \\text{3 boules : } {{${coefBoules}}} \\pi\\ \\text{cm}^{3} \\qquad`
        + ` \\text{Reste : } {{${coefReste}}} \\pi\\ \\text{cm}^{3} = \\dfrac{{{1}}}{{{3}}} \\text{ de la boîte}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une boîte cylindrique de hauteur ${6 * r} cm contient exactement 3 ${objet} identiques de `
        + `rayon ${r} cm empilées : elles touchent les parois et se touchent, le rayon du cylindre vaut `
        + `donc ${r} cm.</p>`
        // « elle ne dépend PAS du rayon ! » était la remarque à faire soi-même.
        + '<p><b>Donne les volumes exacts en complétant les coefficients de π, puis la fraction '
        + 'de la boîte restée vide.</b> Donne une fraction irréductible.</p>',
        { ...opts(L_BOULES), mode: 'saisie' });
    payload._meta.params = { r, coefBoite, coefBoules, coefReste };
    return payload;
};

const GENERATORS = {
    silo_texte_trou_saisie: genSiloTrous,
    gelule_texte_trou_saisie: genGeluleTrous,
    evide_texte_trou_saisie: genEvideTrous,
    boules_texte_trou_saisie: genBoulesTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'silo', label: L_SILO, formatsList: buildFormats('silo') },
    { id: 'gelule', label: L_GELULE, formatsList: buildFormats('gelule') },
    { id: 'evide', label: L_EVIDE, formatsList: buildFormats('evide') },
    { id: 'boules', label: L_BOULES, formatsList: buildFormats('boules') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
